'use client';

import { Header } from '@petsmart-ui/sparky';
import { clsx } from '@libs/common-next';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { Entry } from 'contentful';
import { buildPage } from '../../utils';

type StickyHeaderProps = {
    onTopItems: Entry[];
    top: ReactNode;
    middle: ReactNode;
};

export function StickyHeader({ onTopItems, top, middle }: StickyHeaderProps) {
    const [isSticky, setSticky] = useState(false);
    const topHeaderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            if (!topHeaderRef.current) {
                return;
            }
            const changeToSticky =
                window.scrollY > topHeaderRef.current.offsetHeight;
            setSticky(changeToSticky);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const topHeaderComponents = buildPage(onTopItems);

    const topNavClasses = clsx('upper-nav-container', isSticky && 'hidden');
    const headerClasses = clsx('global-header', isSticky && 'is-sticky');
    const topComponentsClasses = clsx(
        'top-header-components',
        isSticky && 'hidden'
    );

    return (
        <Header className={headerClasses} behavior="sticky">
            <div className={topComponentsClasses}>{topHeaderComponents}</div>
            <div ref={topHeaderRef}>
                <Header.Top className={topNavClasses} data-testid="upper-nav">
                    {top}
                </Header.Top>
            </div>
            {/* hideMenuButton was used because HeaderSparky.Middle assumes that the header
                will span the entire page width. That works with the previous planned design
                but not with the one planned for Velocity. */}
            <Header.Middle className="middle-header-wrapper" hideMenuButton>
                {middle}
            </Header.Middle>
        </Header>
    );
}
